// src/pages/TarifsPage.js
import React from 'react';
import { Helmet } from 'react-helmet-async'; // <<< NOUVEL IMPORT
import './TarifsPage.css';
// ... autres imports (PricingPlanCard, FeatureComparisonTable, etc.)
import PricingPlanCard from '../components/tarifs/PricingPlanCard';
import FeatureComparisonTable from '../components/tarifs/FeatureComparisonTable';
import ServicesAccordion from '../components/services/ServicesAccordion';
import { detailedServicesData } from '../data/detailedServicesData';
import OptionalAddonsSection from '../components/tarifs/OptionalAddonsSection';
import PricingFAQ from '../components/tarifs/PricingFAQ';
import pb from '../lib/pocketbase';

function TarifsPage() {
  const siteUrl = "https://infocolab.com";
  const pageUrl = `${siteUrl}/tarifs`;
  const pageTitle = "Tarifs et Offres Agence Web - Création Site, SEO | INFO COLAB";
  const pageDescription = "Découvrez nos tarifs compétitifs pour la création de sites web, design, SEO et autres services digitaux. INFO COLAB offre des plans adaptés à vos besoins et budget.";
  const ogImageUrl = `${siteUrl}/images/og-tarifs.jpg`; // Crée cette image

  const [remotePlans, setRemotePlans] = React.useState([]);
  const [remoteAddons, setRemoteAddons] = React.useState([]);
  const [remoteComparisonFeatures, setRemoteComparisonFeatures] = React.useState([]);
  const [remoteDetailedServices, setRemoteDetailedServices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const pricingPlans = [
    {
      id: 1,
      title: "Plan Essentiel",
      price: "1 490€",
      term: "projet unique",
      features: [
        "Site vitrine (1 page)",
        "Design responsive",
        "Formulaire de contact",
        "Optimisation SEO de base",
        "Hébergement inclus",
      ],
      ctaText: "Choisir Essentiel",
      ctaLink: "/contact?service=Plan Essentiel",
      isFeatured: false,
      featureDetails: {
        "Nombre de pages initial": "Jusqu'à 1",
        "Design Personnalisé": "oui",
        "Blog intégré": "Optionnel",
        "Optimisation SEO": "De base",
        "Support Technique": "Email (30 jours)",
        "Maintenance évolutive": "Non inclus",
        "Formation à l'outil d'admin": "Non inclus",
      },
    },
    {
      id: 2,
      title: "Plan Pro",
      price: "2 890€",
      term: "projet unique",
      features: [
        "Site vitrine avancé (jusqu'à 15 pages)",
        "Design personnalisé",
        "Formulaire de contact",
        "Blog intégré",
        "Optimisation SEO avancée",
        "Support prioritaire",
        "Hébergement inclus",
      ],
      ctaText: "Opter pour Pro",
      ctaLink: "/contact?service=Plan Pro",
      isFeatured: true,
      featureDetails: {
        "Nombre de pages initial": "Jusqu'à 15",
        "Design Personnalisé": "Oui",
        "Blog intégré": "Oui",
        "Optimisation SEO": "Avancée",
        "Support Technique": "Prioritaire (3 mois)",
        "Maintenance évolutive": "Optionnelle",
        "Formation à l'outil d'admin": "Basique",
      },
    },
    {
      id: 3,
      title: "Plan Sur Mesure",
      price: "Devis",
      term: "selon besoins",
      features: [
        "Site vitrine avancé ou E-commerce",
        "Fonctionnalités complexes",
        "Applications web et mobile",
        "Intégrations API tierces",
        "Gestion base de données",
        "Accompagnement et maintenance",
        "...",
      ],
      ctaText: "Demander un devis",
      ctaLink: "/contact?service=Plan Sur Mesure",
      isFeatured: false,
      featureDetails: {
        "Nombre de pages initial": "Sur mesure",
        "Design Personnalisé": "Oui, avancé",
        "Blog intégré": "Oui",
        "Optimisation SEO": "Stratégie complète",
        "Support Technique": "Dédié",
        "Maintenance évolutive": "Incluse (selon accord)",
        "Formation à l'outil d'admin": "Complète",
      },
    },
  ];

  React.useEffect(() => {
    let mounted = true;
    const fetchPricing = async () => {
      setLoading(true);
      try {
        const [plansRes, optsRes, compFeatRes, compValsRes, detailedCatsRes, detailedPlansRes] = await Promise.all([
          pb.collection('site_pricing_plans').getList(1, 200, { sort: 'sort_order, name', filter: 'is_active = true' }),
          pb.collection('site_pricing_options').getList(1, 200, { sort: 'sort_order, name', filter: 'is_active = true' }),
          pb.collection('site_pricing_comparison_features').getList(1, 500, { sort: 'sort_order, name', filter: 'is_active = true' }),
          pb.collection('site_pricing_comparison_values').getList(1, 5000, { expand: 'feature_id,plan_id' }),
          pb.collection('site_pricing_detailed_categories').getList(1, 500, { sort: 'sort_order, categoryTitle', filter: 'is_active = true' }),
          pb.collection('site_pricing_detailed_plans').getList(1, 2000, { sort: 'sort_order, title', filter: 'is_active = true', expand: 'category_id' }),
        ]);

        const comparisonFeatures = (compFeatRes?.items ?? []).map((f) => String(f?.name || '').trim()).filter(Boolean);

        const comparisonIndex = {};
        (compValsRes?.items ?? []).forEach((v) => {
          const planId = v?.plan_id;
          const featureId = v?.feature_id;
          const featureName = String(v?.expand?.feature_id?.name || '').trim();
          if (!planId || !featureId || !featureName) return;
          const key = `${planId}:${featureName}`;
          comparisonIndex[key] = (v?.value ?? '');
        });

        const plans = (plansRes?.items ?? []).map((p) => {
          const priceLabel = String(p.price_label || '').trim();
          const rawAmount = (typeof p.price_amount === 'number') ? p.price_amount : null;
          const hasAmount = (rawAmount !== null) && !Number.isNaN(rawAmount) && rawAmount !== 0;
          const taxLabel = String(p.price_tax_label || '').trim();
          const price = priceLabel
            ? (hasAmount ? `${priceLabel} ${rawAmount}€${taxLabel ? ` ${taxLabel}` : ''}` : priceLabel)
            : (hasAmount ? `${rawAmount}€${taxLabel ? ` ${taxLabel}` : ''}` : '');
          const term = p.billing_period || '';
          const features = Array.isArray(p.features) ? p.features : (p.features ? p.features : []);
          const fallbackCtaText = 'Demander un devis';
          const fallbackCtaLink = `/contact?service=${encodeURIComponent(p.name || 'Plan')}`;
          const ctaText = String(p?.cta_label || '').trim() || fallbackCtaText;
          const ctaLink = String(p?.cta_url || '').trim() || fallbackCtaLink;
          const ctaNewTab = !!p?.cta_open_in_new_tab;
          const featureDetails = {};
          comparisonFeatures.forEach((fname) => {
            const key = `${p.id}:${fname}`;
            if (Object.prototype.hasOwnProperty.call(comparisonIndex, key)) {
              featureDetails[fname] = String(comparisonIndex[key] ?? '');
            }
          });

          return {
            id: p.id,
            title: p.name || '',
            price,
            term,
            features,
            ctaText,
            ctaLink,
            ctaNewTab,
            isFeatured: !!p.is_featured,
            featureDetails,
          };
        });

        const hasFeatured = plans.some((p) => p?.isFeatured === true);
        if (!hasFeatured && plans.length >= 3) {
          const mid = Math.floor(plans.length / 2);
          plans[mid] = { ...plans[mid], isFeatured: true };
        }

        const addons = (optsRes?.items ?? []).map((o) => {
          const amount = (typeof o.price_amount === 'number') ? o.price_amount : null;
          const hasAmount = (amount !== null) && !Number.isNaN(amount) && amount !== 0;
          const taxLabel = String(o.price_tax_label || '').trim();
          const price = o.price_label
            ? (hasAmount ? `${o.price_label} ${amount}€${taxLabel ? ` ${taxLabel}` : ''}` : o.price_label)
            : (hasAmount ? `${amount}€${taxLabel ? ` ${taxLabel}` : ''}` : '');
          return {
            id: o.id,
            name: o.name || '',
            description: o.description || '',
            price,
            priceNote: '',
          };
        });

        const detailedCategories = (detailedCatsRes?.items ?? []).map((c) => ({
          id: c.id,
          categoryTitle: c?.categoryTitle || c?.category_title || '',
          sort_order: Number(c?.sort_order || 0),
        }));

        const detailedPlans = (detailedPlansRes?.items ?? []).map((p) => ({
          id: p.id,
          category_id: p?.category_id || '',
          title: p?.title || '',
          subtitle: p?.subtitle || '',
          price: p?.price || '',
          price_tax_label: String(p?.price_tax_label || '').trim(),
          priceSuffix: p?.priceSuffix || p?.price_suffix || '',
          features: Array.isArray(p?.features) ? p.features : (p?.features ? p.features : []),
          sort_order: Number(p?.sort_order || 0),
        }));

        const detailedServices = detailedCategories
          .slice()
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((cat) => {
            const catPlans = detailedPlans
              .filter((p) => p.category_id === cat.id)
              .slice()
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((p) => ({
                id: p.id,
                title: p.title,
                subtitle: p.subtitle,
                price: p.price,
                price_tax_label: p.price_tax_label,
                priceSuffix: p.priceSuffix,
                features: Array.isArray(p.features) ? p.features : [],
              }));

            return {
              id: cat.id,
              categoryTitle: cat.categoryTitle,
              plans: catPlans,
            };
          });

        if (mounted) {
          setRemotePlans(plans);
          setRemoteAddons(addons);
          setRemoteComparisonFeatures(comparisonFeatures);
          setRemoteDetailedServices(detailedServices);
        }
      } catch {
        if (mounted) {
          setRemotePlans([]);
          setRemoteAddons([]);
          setRemoteComparisonFeatures([]);
          setRemoteDetailedServices([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchPricing();
    return () => { mounted = false; };
  }, []);

  const plansToShow = remotePlans.length ? remotePlans : pricingPlans;
  const servicesToShow = remoteDetailedServices.length ? remoteDetailedServices : detailedServicesData;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="INFO COLAB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>

      <div className="container">
        <section className="tarifs-page-section">
          <h1>Nos Offres et Tarifs</h1> {/* Gardons le H1 visible */}
          <p className="intro">
            Nous proposons des solutions claires et adaptées à chaque budget et à chaque projet.
            Découvrez nos plans principaux ou explorez l'ensemble de nos prestations en détail.
          </p>
          {/* ... reste du JSX ... */}
          {loading && remotePlans.length === 0 && (
            <p style={{ opacity: 0.8 }}>Chargement…</p>
          )}
          <div className="pricing-plans-grid">{plansToShow.map(plan => (<PricingPlanCard key={plan.id} plan={plan} />))}</div>
        </section>
        <OptionalAddonsSection addons={remoteAddons} />
        <FeatureComparisonTable plans={plansToShow} features={remoteComparisonFeatures} />
        <ServicesAccordion servicesData={servicesToShow} />
      </div>
    </>
  );
}

export default TarifsPage;